// Network utility functions for BSC Testnet

const BSC_TESTNET_CHAIN_ID = '0x61'; // 97 in decimal
const BSC_TESTNET_CHAIN_ID_DECIMAL = 97;

/**
 * Check if the current network is BSC Testnet
 * @returns Promise<boolean> - True if on BSC Testnet, false otherwise
 */
export async function isBSCTestnet(): Promise<boolean> {
  try {
    if (!window.ethereum) {
      return false;
    }
    
    const chainId = await window.ethereum.request({ method: 'eth_chainId' });
    return chainId === BSC_TESTNET_CHAIN_ID || chainId === BSC_TESTNET_CHAIN_ID_DECIMAL.toString();
  } catch (error) {

    return false;
  }
}

/**
 * Switch to BSC Testnet network
 * @returns Promise<void>
 */
export async function ensureBSCTestnet(): Promise<void> {
  try {
    if (!window.ethereum) {
      throw new Error('MetaMask is not installed');
    }

    const isOnCorrectNetwork = await isBSCTestnet();
    if (isOnCorrectNetwork) {
      return; // Already on correct network
    }

    // Try to switch to BSC Testnet
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: BSC_TESTNET_CHAIN_ID }],
      });
    } catch (switchError: any) {
      // This error code indicates that the chain has not been added to MetaMask
      if (switchError.code === 4902) {
        // Add the network to MetaMask
        await window.ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [
            {
              chainId: BSC_TESTNET_CHAIN_ID,
              chainName: 'Binance Smart Chain Testnet',
              nativeCurrency: {
                name: 'Binance Chain Token',
                symbol: 'BNB',
                decimals: 18,
              },
              rpcUrls: ['https://data-seed-prebsc-1-s1.binance.org:8545/'],
              blockExplorerUrls: ['https://testnet.bscscan.com/'],
            },
          ],
        });
      } else {
        throw switchError;
      }
    }
  } catch (error) {

    throw error;
  }
}

/**
 * Get the current network chain ID
 * @returns Promise<string> - The current chain ID
 */
export async function getCurrentChainId(): Promise<string> {
  try {
    if (!window.ethereum) {
      throw new Error('MetaMask is not installed');
    }
    
    const chainId = await window.ethereum.request({ method: 'eth_chainId' });
    return chainId as string;
  } catch (error) {

    throw error;
  }
}

/**
 * Get network name from chain ID
 * @param chainId - The chain ID to get the name for
 * @returns string - The network name
 */
export function getNetworkName(chainId: string | number): string {
  const chainIdStr = typeof chainId === 'number' ? chainId.toString() : chainId;
  
  switch (chainIdStr) {
    case '0x1':
    case '1':
      return 'Ethereum Mainnet';
    case '0x61':
    case '97':
      return 'BSC Testnet';
    case '0x38':
    case '56':
      return 'BSC Mainnet';
    case '0x4':
    case '4':
      return 'Rinkeby Testnet';
    default:
      return 'Unknown Network';
  }
} 